import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useGetOneExpeditionBo } from "@/api/expedition.api";
import { useGetCompanyBOV1 } from "@/api/company.api";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuotationExpeditionForm } from "@/components/backOffice/expeditionForm/quotationExpeditionForm";
import { QuotationUpdateExpeditionForm } from "@/components/backOffice/expeditionForm/quotationUpdateExpeditionForm";
import { CustomQuotationUpdateExpeditionForm } from "@/components/backOffice/expeditionForm/customQuotationUpdateExpeditionForm";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { CustomQuotationExpeditionForm } from "@/components/backOffice/expeditionForm/customQuotationExpeditionForm";
import { FinalizeApi, FinalizeApiEnum } from "@/types/finalizeApi.type";

export const ExpeditionFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(["boExpeditionForm", "common"]);

  const {
    data: expedition,
    isLoading: isLoadingExpedition,
    isError: isErrorExpedition,
  } = useGetOneExpeditionBo(id || "");

  const {
    data: company,
    isLoading: isLoadingCompany,
    isError: isErrorCompany,
  } = useGetCompanyBOV1(expedition?.companyId || "");

  if (isLoadingExpedition || isLoadingCompany) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center p-8">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          <p className="text-sm text-gray-500">{t("page.loading")}</p>
        </div>
      </div>
    );
  }

  if (isErrorExpedition || isErrorCompany || !expedition || !company) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center p-8">
        <div className="flex flex-col items-center gap-4">
          <p className="text-lg font-medium text-gray-900">{t("page.errorTitle")}</p>
          <p className="text-sm text-gray-500">{t("page.errorDescription")}</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            {t("common:actions.back")}
          </Button>
        </div>
      </div>
    );
  }

  const apiEnum: FinalizeApi = (() => {
    if (location.state?.apiEnum && !expedition.draftBy) {
      return location.state.apiEnum as FinalizeApi;
    }
    if (
      expedition.processStatus === ExpeditionProcessStatusEnum.PENDING_PELICAN &&
      expedition.expeditionFedexApi &&
      expedition.expeditionFedexApi !== null
    ) {
      return FinalizeApiEnum.FEDEX;
    } else if (
      expedition.processStatus === ExpeditionProcessStatusEnum.PENDING_PELICAN &&
      expedition.customQuotation &&
      !(
        expedition.customQuotation?.quotationDeliveryContent &&
        expedition.customQuotation?.quotationDeliveryContent.length > 0
      )
    ) {
      return FinalizeApiEnum.NONE_SIMPLIFIED;
    }
    return FinalizeApiEnum.NONE;
  })();

  const modifyMode = location.state?.modifyMode as string | undefined;

  if (expedition.customQuotation) {
    if (modifyMode === "MANUAL_ONLY") {
      const finalizedApiEnum: FinalizeApi = expedition.expeditionFedexApi
        ? FinalizeApiEnum.FEDEX
        : !(
              expedition.customQuotation.quotationDeliveryContent &&
              expedition.customQuotation.quotationDeliveryContent.length > 0
            )
          ? FinalizeApiEnum.NONE_SIMPLIFIED
          : FinalizeApiEnum.NONE;

      return (
        <CustomQuotationUpdateExpeditionForm
          expedition={expedition}
          company={company}
          apiEnum={finalizedApiEnum}
        />
      );
    }

    return (
      <CustomQuotationExpeditionForm expedition={expedition} company={company} apiEnum={apiEnum} />
    );
  }

  if (modifyMode === "MANUAL_ONLY") {
    const finalizedApiEnum = expedition.expeditionFedexApi ? "FEDEX" : "NONE";

    return (
      <QuotationUpdateExpeditionForm
        expedition={expedition}
        company={company}
        apiEnum={finalizedApiEnum}
      />
    );
  }

  return <QuotationExpeditionForm expedition={expedition} company={company} apiEnum={apiEnum} />;
};
